import React, { useState, useEffect, useContext } from 'react';
import Container from 'react-bootstrap/Container';
import Row from 'react-bootstrap/Row';
import Col from 'react-bootstrap/Col';
import Button from 'react-bootstrap/Button';
import Card from 'react-bootstrap/Card';
import Alert from 'react-bootstrap/Alert';
import Badge from 'react-bootstrap/Badge';
import ProgressBar from 'react-bootstrap/ProgressBar';
import Spinner from 'react-bootstrap/Spinner';
import { FaImage, FaStore, FaCheckCircle, FaExclamationTriangle } from 'react-icons/fa';
import { ModalCopyImages } from '../../Components/ModalCopyImages';
import api from '../../Services/api';
import { toast } from 'react-toastify';
import Globals from '../../Globals.json';
import { ThemeContext } from '../../context/theme-context';
import * as S from './styled';

interface Store {
  id: number;
  uuid: string;
  trading_name: string;
  company_name: string;
}

interface CopyResult {
  message: string;
  statistics: {
    products_without_image: number;
    source_products_with_image: number;
    matches_found: number;
    images_copied: number;
    similarity_threshold: number;
  };
  matches: Array<{
    target_product_id: number;
    target_product_name: string;
    source_product_id: number;
    source_product_name: string;
    source_store_id: number;
    similarity: number;
    image_path: string;
    copied: boolean | string;
    error?: string;
  }>;
  dry_run: boolean;
}

export default function CopyProductImages() {
  const { collapsed } = useContext(ThemeContext);

  const [showModal, setShowModal] = useState(false);
  const [stores, setStores] = useState<Store[]>([]);
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState<CopyResult | null>(null);
  const [currentStore, setCurrentStore] = useState<Store | null>(null);

  useEffect(() => {
    loadStores();
    loadCurrentStore();
  }, []);

  const loadStores = async () => {
    try {
      const response = await api.get(Globals.api.stores);
      const storesData = Array.isArray(response.data) 
        ? response.data 
        : (response.data.data || response.data.stores || []);
      
      setStores(storesData);
    } catch (error) {
      toast.error('Erro ao carregar lojas');
      console.error('Erro ao carregar lojas:', error);
    }
  };

  const loadCurrentStore = async () => {
    try {
      const acitvedStoreData = localStorage.getItem('acitvedStore');
      
      if (!acitvedStoreData) {
        toast.error('Loja atual não encontrada no localStorage');
        return;
      }
      
      const parsedStore = JSON.parse(acitvedStoreData);
      
      if (!parsedStore.uuid) {
        toast.error('UUID da loja não encontrado');
        return;
      }
      
      try {
        const response = await api.get(`${Globals.api.stores}/${parsedStore.uuid}`);
        const currentStoreData = response.data.data || response.data;
        setCurrentStore(currentStoreData);
      } catch (apiError) {
        console.warn('Erro ao buscar loja pela API, usando dados do localStorage:', apiError);
        setCurrentStore(parsedStore);
      }
      
    } catch (error) {
      console.error('Erro ao carregar loja atual:', error);
      toast.error('Erro ao identificar loja atual');
    }
  };

  const handleOpenModal = () => {
    setShowModal(true);
  };

  const handleCloseModal = () => {
    setShowModal(false);
  };

  const handleCopyImages = async (selectedStores: number[], threshold: number, dryRun: boolean) => {
    if (!currentStore || !currentStore.uuid) {
      toast.error('Loja atual não identificada. UUID ausente.');
      return;
    }

    setLoading(true);
    setResult(null);

    try {
      const payload = {
        source_store_ids: selectedStores,
        similarity_threshold: threshold,
        dry_run: dryRun,
      };

      const response = await api.post(
        `${Globals.api.stores}/${currentStore.uuid}/copy-product-images`,
        payload
      );

      setResult(response.data);
      
      if (dryRun) {
        toast.info(`Simulação concluída! ${response.data.statistics.matches_found} correspondências encontradas.`);
      } else {
        toast.success(`${response.data.statistics.images_copied} imagens copiadas com sucesso!`);
      }
      
      setShowModal(false);
    } catch (error: any) {
      const errorMessage = error?.response?.data?.error 
        || error?.response?.data?.message 
        || 'Erro ao copiar imagens';
      
      toast.error(errorMessage);
      console.error('Erro completo:', error);
    } finally {
      setLoading(false);
    }
  };

  const getSuccessRate = () => {
    if (!result) return 0;
    const { matches_found, images_copied } = result.statistics;
    if (matches_found === 0) return 0;
    return (images_copied / matches_found) * 100;
  };

  const availableStores = stores.filter(s => s.id !== currentStore?.id);

  return (
    <Container className={collapsed ? 'contenteLayoutCollapsed' : 'contenteLayout'}>
      <S.PageWrapper>
        {/* Header */}
        <S.HeaderCard>
          <Card>
            <Card.Body>
              <S.HeaderContent>
                <FaImage size={28} />
                <div>
                  <h4>Copiar Imagens de Produtos</h4>
                  <p>Copie automaticamente imagens de produtos de outras lojas para produtos sem imagem na sua loja.</p>
                </div>
              </S.HeaderContent>
            </Card.Body>
          </Card>
        </S.HeaderCard>

        {/* Info Cards */}
        <S.InfoCardsRow>
          <Row>
            <Col md={4}>
              <S.InfoCard>
                <Card className="h-100">
                  <Card.Body>
                    <S.InfoCardContent>
                      <S.IconCircle className="bg-primary">
                        <FaStore size={20} />
                      </S.IconCircle>
                      <div>
                        <S.InfoLabel>Loja Atual</S.InfoLabel>
                        <S.InfoValue>
                          {currentStore?.trading_name || 'Carregando...'}
                        </S.InfoValue>
                        {currentStore?.uuid && (
                          <S.InfoSubtext>UUID: {currentStore.uuid.substring(0, 8)}...</S.InfoSubtext>
                        )}
                      </div>
                    </S.InfoCardContent>
                  </Card.Body>
                </Card>
              </S.InfoCard>
            </Col>

            <Col md={4}>
              <S.InfoCard>
                <Card className="h-100">
                  <Card.Body>
                    <S.InfoCardContent>
                      <S.IconCircle className="bg-info">
                        <FaImage size={20} />
                      </S.IconCircle>
                      <div>
                        <S.InfoLabel>Modo Inteligente</S.InfoLabel>
                        <S.InfoValue>Similaridade de Nome</S.InfoValue>
                      </div>
                    </S.InfoCardContent>
                  </Card.Body>
                </Card>
              </S.InfoCard>
            </Col>

            <Col md={4}>
              <S.InfoCard>
                <Card className="h-100">
                  <Card.Body>
                    <S.InfoCardContent>
                      <S.IconCircle className="bg-success">
                        <FaCheckCircle size={20} />
                      </S.IconCircle>
                      <div>
                        <S.InfoLabel>Lojas Disponíveis</S.InfoLabel>
                        <S.InfoValue>{availableStores.length} lojas</S.InfoValue>
                      </div>
                    </S.InfoCardContent>
                  </Card.Body>
                </Card>
              </S.InfoCard>
            </Col>
          </Row>
        </S.InfoCardsRow>

        {/* Action Card */}
        <S.ActionCard>
          <Card>
            <Card.Body>
              <Row>
                <Col md={8}>
                  <S.ActionTitle>
                    <FaImage />
                    Como Funciona?
                  </S.ActionTitle>
                  
                  <S.StepsList>
                    <S.StepItem>
                      <S.StepBadge>1</S.StepBadge>
                      <div>
                        <S.StepTitle>Seleção de lojas de referência</S.StepTitle>
                        <S.StepDescription>
                          Escolha uma ou mais lojas que possuem produtos com imagens.
                        </S.StepDescription>
                      </div>
                    </S.StepItem>

                    <S.StepItem>
                      <S.StepBadge>2</S.StepBadge>
                      <div>
                        <S.StepTitle>Análise inteligente</S.StepTitle>
                        <S.StepDescription>
                          O sistema compara os nomes dos produtos e identifica correspondências com base na similaridade.
                        </S.StepDescription>
                      </div>
                    </S.StepItem>

                    <S.StepItem>
                      <S.StepBadge>3</S.StepBadge>
                      <div>
                        <S.StepTitle>Cópia automática</S.StepTitle>
                        <S.StepDescription>
                          As imagens dos produtos correspondentes são copiadas automaticamente para os seus produtos.
                        </S.StepDescription>
                      </div>
                    </S.StepItem>
                  </S.StepsList>
                </Col>

                <Col md={4} className="d-flex align-items-center">
                  <Button
                    variant="primary"
                    size="lg"
                    className="w-100"
                    onClick={handleOpenModal}
                    disabled={loading || !currentStore || availableStores.length === 0}
                  >
                    {loading ? (
                      <>
                        <Spinner animation="border" size="sm" className="me-2" />
                        Processando...
                      </>
                    ) : (
                      <>
                        <FaImage className="me-2" />
                        Iniciar Cópia
                      </>
                    )}
                  </Button>
                </Col>
              </Row>
            </Card.Body>
          </Card>
        </S.ActionCard>

        {/* Results Section */}
        {result && (
          <>
            {/* Statistics */}
            <S.ResultsCard>
              <Card>
                <Card.Body>
                  <S.ResultsTitle>
                    {result.dry_run ? (
                      <>
                        <FaExclamationTriangle className="text-warning" />
                        Resultado da Simulação
                      </>
                    ) : (
                      <>
                        <FaCheckCircle className="text-success" />
                        Resultado da Cópia
                      </>
                    )}
                  </S.ResultsTitle>

                  {result.dry_run && (
                    <Alert variant="warning" className="mb-4">
                      <strong>Atenção:</strong> Esta foi apenas uma simulação. Nenhuma imagem foi copiada.
                    </Alert>
                  )}

                  <Row>
                    <Col md={3}>
                      <S.StatCard className="bg-light">
                        <S.StatValue className="text-primary">
                          {result.statistics.products_without_image}
                        </S.StatValue>
                        <S.StatLabel>Produtos Sem Imagem</S.StatLabel>
                      </S.StatCard>
                    </Col>

                    <Col md={3}>
                      <S.StatCard className="bg-light">
                        <S.StatValue className="text-info">
                          {result.statistics.matches_found}
                        </S.StatValue>
                        <S.StatLabel>Correspondências</S.StatLabel>
                      </S.StatCard>
                    </Col>

                    <Col md={3}>
                      <S.StatCard className="bg-light">
                        <S.StatValue className="text-success">
                          {result.statistics.images_copied}
                        </S.StatValue>
                        <S.StatLabel>Imagens Copiadas</S.StatLabel>
                      </S.StatCard>
                    </Col>

                    <Col md={3}>
                      <S.StatCard className="bg-light">
                        <S.StatValue className="text-secondary">
                          {result.statistics.similarity_threshold}%
                        </S.StatValue>
                        <S.StatLabel>Threshold Usado</S.StatLabel>
                      </S.StatCard>
                    </Col>
                  </Row>

                  {result.statistics.matches_found > 0 && (
                    <S.ProgressWrapper>
                      <S.ProgressHeader>
                        <span>Taxa de Sucesso</span>
                        <span>{getSuccessRate().toFixed(1)}%</span>
                      </S.ProgressHeader>
                      <ProgressBar 
                        now={getSuccessRate()} 
                        variant={getSuccessRate() > 80 ? 'success' : getSuccessRate() > 50 ? 'warning' : 'danger'}
                      />
                    </S.ProgressWrapper>
                  )}
                </Card.Body>
              </Card>
            </S.ResultsCard>

            {/* Matches Table */}
            {result.matches.length > 0 && (
              <S.TableCard>
                <Card>
                  <Card.Body>
                    <S.TableTitle>
                      Correspondências Encontradas ({result.matches.length})
                    </S.TableTitle>
                    
                    <S.TableWrapper>
                      <table className="table table-hover">
                        <thead>
                          <tr>
                            <th>Produto (Sua Loja)</th>
                            <th>Produto (Origem)</th>
                            <th>Loja Origem</th>
                            <th>Similaridade</th>
                            <th>Status</th>
                          </tr>
                        </thead>
                        <tbody>
                          {result.matches.map((match, index) => (
                            <tr key={index}>
                              <td>
                                <S.ProductName>{match.target_product_name}</S.ProductName>
                                <S.ProductId>ID: {match.target_product_id}</S.ProductId>
                              </td>
                              <td>
                                <S.ProductName>{match.source_product_name}</S.ProductName>
                                <S.ProductId>ID: {match.source_product_id}</S.ProductId>
                              </td>
                              <td>
                                <Badge bg="secondary">
                                  {stores.find(s => s.id === match.source_store_id)?.trading_name || `Store ID: ${match.source_store_id}`}
                                </Badge>
                              </td>
                              <td>
                                <Badge bg={match.similarity >= 95 ? 'success' : match.similarity >= 85 ? 'info' : 'warning'}>
                                  {match.similarity.toFixed(1)}%
                                </Badge>
                              </td>
                              <td>
                                {match.copied === true && (
                                  <Badge bg="success">
                                    <FaCheckCircle className="me-1" />
                                    Copiado
                                  </Badge>
                                )}
                                {match.copied === 'dry_run' && (
                                  <Badge bg="warning">Simulação</Badge>
                                )}
                                {match.copied === false && (
                                  <Badge bg="danger">
                                    <FaExclamationTriangle className="me-1" />
                                    {match.error || 'Erro'}
                                  </Badge>
                                )}
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </S.TableWrapper>
                  </Card.Body>
                </Card>
              </S.TableCard>
            )}
          </>
        )}
      </S.PageWrapper>

      <ModalCopyImages
        show={showModal}
        onHide={handleCloseModal}
        stores={availableStores}
        onCopy={handleCopyImages}
        loading={loading}
      />
    </Container>
  );
}